import { BadgeIndianRupee, Printer } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  printSaleBill,
  SaleBill,
  SaleBillPrintCopy,
  type SaleBillProps,
  type SaleBillRow,
} from '@/shared/ui';
import { formatDate } from '@/shared/utils/date';

import type { StaticOrderDetail } from '../lib/orderDetailStatic';
import { orderStatusIcon, orderStatusTone } from '../lib/orderStatusPresentation';

export interface OrderBillDialogProps {
  detail: StaticOrderDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function OrderBillDialog({ detail, open, onOpenChange }: OrderBillDialogProps) {
  const { t } = useTranslation('orders');
  const billProps = open ? orderBillProps(detail, t) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex max-h-[min(44rem,calc(100vh-2rem))] w-[calc(100vw-2rem)] max-w-lg flex-col overflow-hidden p-0"
        showClose
      >
        {billProps ? (
          <>
            <div className="border-b border-border px-5 py-4 pr-14">
              <DialogTitle>{t('detail.bill.title')}</DialogTitle>
              <p className="mt-1 text-sm text-fg-muted">{t('detail.bill.subtitle')}</p>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
              <SaleBill {...billProps} />
            </div>
            <div className="flex flex-col gap-2 border-t border-border px-5 py-4 sm:flex-row">
              <Button type="button" variant="secondary" className="flex-1" onClick={printSaleBill}>
                <Printer aria-hidden="true" className="h-4 w-4" strokeWidth={2} />
                {t('detail.bill.print')}
              </Button>
              <Button type="button" className="flex-1" onClick={() => onOpenChange(false)}>
                {t('detail.bill.close')}
              </Button>
            </div>
          </>
        ) : null}
      </DialogContent>
      {billProps ? <SaleBillPrintCopy {...billProps} /> : null}
    </Dialog>
  );
}

function present(value: string | undefined): string | undefined {
  const trimmed = value?.trim() ?? '';
  if (!trimmed || trimmed === '—') return undefined;
  return trimmed;
}

function orderBillProps(
  detail: StaticOrderDetail,
  t: (key: string, options?: Record<string, unknown>) => string,
): Omit<SaleBillProps, 'printCopy'> {
  const storeLabel = [present(detail.hubCode), present(detail.hubName)].filter(Boolean).join(' · ');
  const discountPaise = detail.discountPaise ?? 0;
  const handlingFeePaise = detail.handlingFeePaise ?? 0;
  const rows: SaleBillRow[] = [{ label: t('detail.summary.subtotal'), amountPaise: detail.subtotalPaise }];
  if (discountPaise > 0) {
    rows.push({ label: t('detail.summary.discount'), amountPaise: discountPaise, savings: true });
  }
  if (detail.taxPaise > 0) {
    rows.push({ label: t('detail.summary.taxGst'), amountPaise: detail.taxPaise });
  }
  if (handlingFeePaise > 0) {
    rows.push({ label: t('detail.summary.handlingFee'), amountPaise: handlingFeePaise });
  }
  if (detail.deliveryFeePaise > 0) {
    rows.push({ label: t('detail.summary.deliveryFee'), amountPaise: detail.deliveryFeePaise });
  }

  const paid = detail.payment.isPaid;
  const collected = detail.payment.collectedAmountPaise;
  const paidPaise = paid && collected && collected > 0 ? collected : detail.totalPaise;

  return {
    brand: t('detail.bill.brand'),
    subtitle: t(detail.payment.methodLabelKey, { method: detail.payment.method }),
    storeLabel: storeLabel || undefined,
    soldAtLabel: formatDate(detail.placedAtIso, 'dateTime'),
    orderNumber: detail.orderNumber || detail.id,
    orderId: detail.id,
    pills: [
      {
        label: t(`status.${detail.status}`),
        tone: orderStatusTone[detail.status],
        icon: orderStatusIcon[detail.status],
      },
      {
        label: t(detail.payment.methodLabelKey, { method: detail.payment.method }),
        tone: paid ? 'success' : 'warning',
        icon: BadgeIndianRupee,
      },
    ],
    customerLabel: t('detail.bill.customer'),
    customerName: present(detail.customer.name),
    customerPhone: present(detail.customer.phoneDisplay),
    emptyCustomerLabel: t('detail.bill.customerUnknown'),
    itemsLabel: t('detail.bill.items'),
    formatLineMeta: (quantity) => t('detail.bill.lineMeta', { quantity }),
    lines: detail.lines.map((line) => ({
      id: line.id,
      name: line.name,
      quantity: line.quantity,
      unitPricePaise: line.unitPricePaise,
      lineTotalPaise: line.lineTotalPaise,
      mrpPaise: line.mrpPaise,
    })),
    rows,
    payableLabel: t('detail.summary.orderTotal'),
    payablePaise: detail.totalPaise,
    paidLabel: paid ? t('detail.payment.paid') : t('detail.bill.amountDue'),
    paidPaise,
  };
}
